import { z } from "zod";

/**
 * Canonical domain contract for the tire-storage app.
 *
 * This file is the interface between the persistence layer (`@skladiscenje/db`)
 * and the domain/API layer (`@skladiscenje/core`, `apps/server`). Both sides code
 * against it, so it is owned centrally rather than by either side.
 *
 * ## Two levels of strictness
 *
 * Nine years of production data are being imported and that data is dirty — see
 * `IMPORT-POLICY.md`. Roughly 43% of rows never recorded a rim type, 86% never
 * recorded a phone number, 39 rows have no slot number at all, and six carry
 * dates that are not dates (`27.32.019`). None of that can be discarded: it is
 * the shop's actual history.
 *
 * So the contract splits:
 *
 *   - {@link warehouseItem} — the *stored* shape. Permissive. Anything the shop
 *     ever recorded must round-trip through it. Missing data is `null`, never a
 *     guess.
 *   - {@link newItemInput} — the *creation* shape. Strict. Everything the counter
 *     workflow genuinely needs is required, so new data cannot join the mess.
 *
 * Read a `null` as "never recorded", not as "empty".
 *
 * Naming is English even though the UI is Slovenian; the UI holds its own labels.
 * Where a rule is inherited from the legacy WPF app, the source is cited so the
 * behaviour can be traced back.
 */

/** Rim material. Legacy stored these in mixed case (`Alu`, `ALU`, `Fe`, `FE`, `BREZ`). */
export const rimsType = z.enum(["ALU", "FE", "BREZ"]);
export type RimsType = z.infer<typeof rimsType>;

/**
 * Remaining tread, as a closed set of buckets — the legacy UI offered exactly
 * these five radio buttons (AddItemWindow.xaml:37-44,104-105).
 *
 * The imported data also contains `20%`, `60%` and a bare `50`; the importer
 * coerces what it safely can and nulls the rest. See `IMPORT-POLICY.md`.
 */
export const tireUsage = z.enum(["100%", "80%", "50%", "30%", "0%"]);
export type TireUsage = z.infer<typeof tireUsage>;

/** "Letne" = summer, "Zimske" = winter. Legacy built season names as `"<Kind> <Year>"`. */
export const seasonKind = z.enum(["letne", "zimske"]);
export type SeasonKind = z.infer<typeof seasonKind>;

export const seasonId = z.string().uuid().brand<"SeasonId">();
export const itemId = z.string().uuid().brand<"ItemId">();
export const employeeId = z.string().uuid().brand<"EmployeeId">();

export type SeasonId = z.infer<typeof seasonId>;
export type ItemId = z.infer<typeof itemId>;
export type EmployeeId = z.infer<typeof employeeId>;

/**
 * A storage season. Replaces the legacy scheme where the season was free text on
 * every row and the season list was derived with a `Distinct` query (DAO.cs:47).
 *
 * At most one season has `isOpen: true`; it is the bucket new intake lands in,
 * replacing the `"Nova sezona"` sentinel string (DAO.cs:16).
 *
 * `kind`/`year` are null **only while a season is open**. This mirrors the shop's
 * actual workflow: intake accumulates in an unnamed bucket, and the season is
 * named ("Letne 2024") at the moment it is closed — see the close dialog in
 * MainWindow.xaml:80-81 and `finishSeason` (DAO.cs:79). A closed season always
 * has both, which {@link closedSeason} enforces.
 */
export const season = z.object({
  id: seasonId,
  kind: seasonKind.nullable(),
  year: z.number().int().min(2000).max(2100).nullable(),
  isOpen: z.boolean(),
  closedAt: z.date().nullable(),
  createdAt: z.date(),
});
export type Season = z.infer<typeof season>;

/** A closed season is fully named; this is the invariant `closeSeason` must establish. */
export const closedSeason = season.extend({
  kind: seasonKind,
  year: z.number().int().min(2000).max(2100),
  isOpen: z.literal(false),
  closedAt: z.date(),
});
export type ClosedSeason = z.infer<typeof closedSeason>;

/**
 * A shop employee. The `code` is the short PIN typed at the counter to authorise
 * a save or delete; it is an attribution mechanism, never an access-control
 * boundary (see DAO.getUserWithCode, DAO.cs:336).
 */
export const employee = z.object({
  id: employeeId,
  name: z.string().min(1),
  lastname: z.string().min(1),
  code: z.string().min(2).max(8),
  active: z.boolean(),
});
export type Employee = z.infer<typeof employee>;

/** Employee with the PIN stripped. Anything leaving the API uses this shape. */
export const publicEmployee = employee.omit({ code: true });
export type PublicEmployee = z.infer<typeof publicEmployee>;

/**
 * What the setup wizard / settings screen sends to create an employee — the
 * PIN-bearing fields, minus the server-assigned `id` and the `active` flag
 * (a freshly created employee is always active). `code` carries the same
 * `min(2).max(8)` shape as `employee.code`; the database's `employees_code_idx`
 * is what ultimately guarantees it is unique.
 */
export const newEmployeeInput = employee.omit({ id: true, active: true });
export type NewEmployeeInput = z.infer<typeof newEmployeeInput>;

/**
 * Fields the user-admin / settings screen can change on an existing employee.
 * Every field is optional — `undefined` means "leave as is".
 *
 * `code` (the PIN) is editable: an admin manages employee codes directly (see
 * `routes/employees.ts`), and re-pointing one is safe because attribution is by
 * employee `id` — the `audit_log.employee_id` FK, never the code itself — so
 * changing a PIN never rewrites who did what. The `employees_code_idx` unique
 * constraint still rejects a collision, surfaced as a `duplicate-code` outcome
 * exactly as on create.
 */
export const employeePatch = z
  .object({
    name: z.string().min(1),
    lastname: z.string().min(1),
    active: z.boolean(),
    code: z.string().min(2).max(8),
  })
  .partial();
export type EmployeePatch = z.infer<typeof employeePatch>;

/**
 * One set of tires, as stored.
 *
 * Legacy denormalised exactly two sets into paired columns (`dimension1`/
 * `dimension2`, …) gated by a `twoSets` string flag, and when the flag was false
 * it copied set 1 into set 2 (AddItemWindow.xaml.cs:454-461). Here a stored item
 * has one or two rows and that fallback disappears — the importer must not
 * create a second row for single-set legacy records.
 */
export const tireSet = z.object({
  ordinal: z.union([z.literal(1), z.literal(2)]),
  dimension: z.string().nullable(),
  quantity: z.number().int().min(1).max(9).nullable(),
  maker: z.string().nullable(),
  usage: tireUsage.nullable(),
  rimsType: rimsType.nullable(),
});
export type TireSet = z.infer<typeof tireSet>;

/**
 * A stored set of tires belonging to one customer for one season.
 *
 * `number` is the physical storage slot number, unique within a season among
 * non-deleted items, allocated by first-gap search so a deleted entry's number is
 * reused (DAO.getNumberForSeason, DAO.cs:164 — a business rule: the shop's racks
 * are physically numbered). It is nullable only for legacy rows that never had
 * one; {@link newItemInput} always assigns one.
 */
export const warehouseItem = z.object({
  id: itemId,
  seasonId: seasonId,
  number: z.number().int().positive().nullable(),

  // Customer. All nullable — legacy rows are frequently missing name or phone.
  name: z.string().nullable(),
  lastname: z.string().nullable(),
  phone: z.string().nullable(),
  carMaker: z.string().nullable(),
  registrationNumber: z.string().nullable(),

  tireSets: z.array(tireSet).min(1).max(2),

  notes: z.string().nullable(),
  /** Customer paid for rim washing. Drives the green block on the receipt tag. */
  washing: z.boolean(),

  receivedAt: z.date().nullable(),
  receivedBy: publicEmployee.nullable(),

  pickedUp: z.boolean(),
  pickedUpAt: z.date().nullable(),
  issuedBy: publicEmployee.nullable(),

  deleted: z.boolean(),
  deletedAt: z.date().nullable(),
  deletedBy: publicEmployee.nullable(),

  /**
   * Verbatim source document for rows that came from Mongo, kept so the import
   * stays auditable and re-derivable. `null` for anything created since. Never
   * read this for business logic.
   */
  legacy: z
    .object({
      mongoId: z.string(),
      /** The original document, unmodified. */
      raw: z.record(z.unknown()),
      /** Coercions and drops applied on import, for the migration report. */
      notes: z.array(z.string()),
    })
    .nullable(),
});
export type WarehouseItem = z.infer<typeof warehouseItem>;

/**
 * What the counter must supply to create a new entry. Strict on purpose: this is
 * the boundary that stops new records inheriting the legacy data's gaps.
 *
 * `number` is absent — the server allocates it (first-gap, inside the write
 * transaction), never the client.
 */
export const newItemInput = z.object({
  lastname: z.string().trim().min(1),
  name: z.string().trim().min(1),
  phone: z.string().trim(),
  carMaker: z.string().trim().min(1),
  registrationNumber: z.string().trim(),
  /**
   * One or two sets, numbered from 1 with no gaps.
   *
   * The ordinal constraints are enforced here rather than left to the caller
   * because the legacy UI made them structurally impossible — set 1 and set 2
   * were fixed, separate controls (AddItemWindow.xaml:84-91) — whereas a JSON
   * payload can trivially send two sets both tagged `1`, or a lone set tagged
   * `2`. Nothing downstream should have to re-check this.
   */
  tireSets: z
    .array(
      z.object({
        ordinal: z.union([z.literal(1), z.literal(2)]),
        dimension: z.string().trim().min(1),
        quantity: z.number().int().min(1).max(9),
        maker: z.string().trim().min(1),
        usage: tireUsage,
        rimsType: rimsType,
      }),
    )
    .min(1)
    .max(2)
    .superRefine((sets, ctx) => {
      const ordinals = sets.map((s) => s.ordinal);
      if (new Set(ordinals).size !== ordinals.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "duplicate_ordinal" });
      }
      if (!ordinals.includes(1)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "missing_primary_set" });
      }
    }),
  notes: z.string().trim(),
  washing: z.boolean(),
  /** Employee PIN authorising the write. Verified server-side, never stored raw. */
  employeeCode: z.string().min(2).max(8),
  /**
   * The customer's existing stored item being handed back in the same visit, if
   * any. Marked picked-up in the same transaction as the new intake — legacy did
   * these as two independent writes that could half-fail
   * (AddItemWindow.xaml.cs:345-353).
   */
  swapOutItemId: itemId.nullable(),
});
export type NewItemInput = z.infer<typeof newItemInput>;

/**
 * Row-highlight rule, ported verbatim from `WarehouseItem.redColor`
 * (WarehouseItem.cs:47): flag the row when it carries notes, or when any set is
 * worn to 30% or less. Lives in the contract because the list view and the API's
 * list projection must agree.
 */
export function isFlagged(item: Pick<WarehouseItem, "notes" | "tireSets">): boolean {
  const worn = (u: TireUsage | null) => u === "30%" || u === "0%";
  return (item.notes ?? "").trim().length > 0 || item.tireSets.some((s) => worn(s.usage));
}
